// Basics floor for RevOps V2 (build Checkpoint 1).
//
// DO NOT REMOVE — growth formula (plain rungs 1–10):
//   Docs/RevOps-Growth-Order-Cluster-Skill.md
//   Docs/RevOps-Build-Checkpoints.md
// Checkpoint 1 judges growth rung 2 only (peer-on-a-wire basics).
// Rung 1 house pitch = Checkpoint 2. CRM = red "Not scoring yet" until later.
//
// Person identity: Meet Recorder owns diarization + who joined (names).
// RevOps only scores rows with a confirmed employee match — no second ID invent.
//
// Cost: this module only reads SellerCallScore rows already stored. It does not
// call Echo. Backfill/rescore spend is separate (Checkpoint backfill notes).
// Meet Recorder filler accuracy: if transcripts omit fillers, voice_delivery /
// filler_per_100w will look too clean — flag at backfill time; prefer a raw
// transcript only when it adds no extra model cost.

import { companyOf, formatDate, isRefused } from "./callMeta.js";

/** Pass mark for the basics floor. Change anytime — not sacred. */
export const BASICS_PASS = 8;

/** How many recent scored calls feed the headline card (deepens as they pile up). */
export const FLOOR_WINDOW = 5;

/** Soft-drop a call from the standing if it swings this far from the person's median. */
export const ANOMALY_SWING = 3;

/**
 * Parameter keys allowed on the V2 radar / floor standing.
 * Everything else stays off the radar (may appear as red "Not scoring yet").
 */
export const FLOOR_PARAM_KEYS = Object.freeze([
  "voice_delivery",
  "room_presence",
]);

/** Plain labels — never letter-codes or "layer" talk on the screen. */
export const FLOOR_LINES = Object.freeze([
  {
    id: "fillers_flow",
    label: "Fillers & flow",
    paramKeys: ["voice_delivery"],
    measureIds: ["filler_per_100w"],
  },
  {
    id: "pace",
    label: "Pace",
    paramKeys: ["voice_delivery", "room_presence"],
    measureIds: ["speaking_pace_wpm"],
  },
  {
    id: "one_question",
    label: "One question at a time",
    floorSkills: ["C.7"],
    paramKeys: [],
  },
  {
    id: "no_junior_leaks",
    label: "No sorry / “still learning” leaks",
    floorSkills: ["C.4", "C.5"],
    paramKeys: [],
  },
  {
    id: "silence_after_ask",
    label: "Silence after your question",
    floorSkills: ["A.3"],
    paramKeys: [],
  },
]);

/** Planned rungs we are not judging on Checkpoint 1 — red chips. */
export const NOT_SCORING_YET = Object.freeze([
  { id: "house", label: "House pitch / say what we sell", rung: 1 },
  { id: "crm", label: "CRM hygiene", rung: 3 },
  { id: "camera", label: "Camera & stillness", rung: 4 },
  { id: "ask", label: "Ask & micro-close", rung: 5 },
  { id: "room", label: "Enter the room / frame", rung: 6 },
  { id: "discovery", label: "Discovery depth", rung: 7 },
  { id: "close", label: "Demo & close loop", rung: 8 },
  { id: "mid", label: "Mid-room / multi-thread", rung: 9 },
  { id: "hard", label: "Harder geography bar", rung: 10 },
]);

function callTime(c) {
  return c.started_at || c.call_date || "";
}

/** Newest → oldest scored sales calls (not refused, not CRM-only). */
export function scoredCallsNewestFirst(calls) {
  return [...(calls || [])]
    .filter((c) => !isRefused(c) && c.source !== "crm")
    .sort((a, b) => callTime(b).localeCompare(callTime(a)) || (b.id - a.id));
}

function paramScore(call, key) {
  const p = (call.parameters || []).find((x) => x.key === key);
  if (!p || p.observed === false || typeof p.score !== "number") return null;
  return p.score;
}

function median(nums) {
  if (!nums.length) return null;
  const s = [...nums].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
}

/**
 * Mark calls whose floor average swings hard vs the person's other calls.
 * Those still show on the daily read; they do not rewrite the standing.
 */
export function tagAnomalies(callsNewestFirst) {
  const withAvg = callsNewestFirst.map((c) => {
    const scores = FLOOR_PARAM_KEYS.map((k) => paramScore(c, k)).filter((n) => n != null);
    const avg = scores.length
      ? scores.reduce((a, b) => a + b, 0) / scores.length
      : null;
    return { call: c, avg };
  });
  const priors = withAvg.map((x) => x.avg).filter((n) => n != null);
  return withAvg.map((row, i) => {
    if (row.avg == null || priors.length < 3) {
      return { ...row, anomaly: false };
    }
    const others = withAvg
      .filter((_, j) => j !== i && withAvg[j].avg != null)
      .map((x) => x.avg);
    const med = median(others);
    const anomaly = med != null && Math.abs(row.avg - med) >= ANOMALY_SWING;
    return { ...row, anomaly };
  });
}

function floorChecksFromMeasures(measures) {
  const list = (measures && measures.floor_checks) || [];
  const by = {};
  for (const f of list) {
    if (f && f.skill) by[f.skill] = f;
  }
  return by;
}

/**
 * Build the basics standing for a person from their call list (+ optional
 * detail payloads keyed by call id, for measures / floor_checks).
 */
export function buildBasicsStanding(calls, detailsById = {}) {
  const newest = scoredCallsNewestFirst(calls);
  const tagged = tagAnomalies(newest);
  const window = tagged.slice(0, FLOOR_WINDOW);
  const forStanding = window.filter((t) => !t.anomaly);
  const sample = forStanding.length ? forStanding : window;

  const windowCalls = sample.map((t) => t.call);
  const n = windowCalls.length;

  const lines = FLOOR_LINES.map((line) => {
    const scores = [];
    let proof = null;
    let measureHint = null;
    let floorMissCount = 0;
    let floorSeen = false;

    for (const c of windowCalls) {
      const detail = detailsById[c.id] || c;
      for (const key of line.paramKeys || []) {
        const s = paramScore(detail, key);
        if (s != null) {
          scores.push(s);
          if (!proof || s < (proof.score ?? 99)) {
            proof = {
              score: s,
              callId: c.id,
              company: companyOf(c),
              date: formatDate(c),
              at: null,
            };
          }
        }
      }
      const measures = detail.measures;
      if (measures && line.measureIds) {
        for (const mid of line.measureIds) {
          const v = measures[mid];
          if (typeof v === "number") {
            measureHint = { id: mid, value: v };
          }
        }
      }
      if (line.floorSkills && measures) {
        const fc = floorChecksFromMeasures(measures);
        for (const sk of line.floorSkills) {
          const hit = fc[sk];
          if (hit && typeof hit.count === "number") {
            floorSeen = true;
            floorMissCount += hit.count;
            if (hit.count > 0 && hit.examples && hit.examples[0] && !proof) {
              proof = {
                score: null,
                callId: c.id,
                company: companyOf(c),
                date: formatDate(c),
                at: hit.examples[0].at_seconds,
                heard: hit.examples[0].heard,
              };
            }
          }
        }
      }
    }

    let score = null;
    let status = "not_measured";
    if (scores.length) {
      score = Math.round((scores.reduce((a, b) => a + b, 0) / scores.length) * 10) / 10;
      status = score >= BASICS_PASS ? "pass" : "fail";
    } else if (floorSeen) {
      // Floor checks: target is zero hits. Map to a simple 10 / miss.
      score = floorMissCount === 0 ? 10 : Math.max(0, 10 - floorMissCount * 2);
      status = floorMissCount === 0 ? "pass" : "fail";
    }

    return {
      ...line,
      score,
      status,
      proof,
      measureHint,
      samples: scores.length || (floorSeen ? windowCalls.length : 0),
    };
  });

  const measured = lines.filter((l) => l.status !== "not_measured");
  const failing = measured.filter((l) => l.status === "fail");
  const passing = measured.filter((l) => l.status === "pass");
  const floorAvg = measured.length
    ? Math.round(
      (measured.reduce((a, l) => a + (l.score ?? 0), 0) / measured.length) * 10,
    ) / 10
    : null;

  const radarAxes = FLOOR_PARAM_KEYS.map((key) => {
    const vals = [];
    for (const c of windowCalls) {
      const s = paramScore(c, key);
      if (s != null) vals.push(s);
    }
    const label =
      key === "voice_delivery" ? "Fillers & flow"
        : key === "room_presence" ? "Presence in the room"
          : key;
    return {
      key,
      label,
      dimension: "tonality",
      score: vals.length
        ? Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10
        : null,
      observed: vals.length > 0,
    };
  }).filter((a) => a.observed);

  // Radar needs ≥3 axes. Fold transcript hygiene lines into one more axis when present.
  const hygiene = lines.filter((l) => l.floorSkills && l.status !== "not_measured");
  if (hygiene.length) {
    const avg = hygiene.reduce((a, l) => a + (l.score ?? 0), 0) / hygiene.length;
    radarAxes.push({
      key: "speech_hygiene",
      label: "Speech hygiene",
      dimension: "tonality",
      score: Math.round(avg * 10) / 10,
      observed: true,
    });
  }

  const anomalyCount = window.filter((t) => t.anomaly).length;

  return {
    n,
    floorAvg,
    lines,
    failing,
    passing,
    radarAxes,
    anomalyCount,
    windowLabel: n === 0
      ? "No scored calls yet"
      : n === 1
        ? "Scored over 1 call (early read — thin sample)"
        : `Scored over the last ${n} calls`,
    newestCallId: newest[0]?.id ?? null,
  };
}

/** Keep pattern drills on the floor only for Checkpoint 1. */
export function isFloorMistakeKey(key) {
  return FLOOR_PARAM_KEYS.includes(key);
}
